import { Condition } from '@toggle/toggle';
import React, { useEffect, useMemo, useState } from 'react';

import { useFilteredTimeSeries } from '~/react-query-hooks/snakes/useTimeSeries';

import { ConditionSearch } from '../condition-search/ConditionSearch';
import * as S from './ConditionDataList.styles';

export interface ConditionDataListProps {
  condition: Condition;
  response: (editedPayload: Partial<Condition>) => void;
}

interface Item {
  value: string;
  label: string;
}

export const ConditionDataList = ({
  condition,
  response,
}: ConditionDataListProps) => {
  const { data: snakeItems } = useFilteredTimeSeries([condition.entity]);
  const [items, setItems] = useState<Item[]>([]);
  const [search, setSearch] = useState('');

  const data = useMemo(
    () =>
      snakeItems
        ?.filter(({ search: { rank } }) => rank !== -1)
        .sort((a, b) => b.search.rank - a.search.rank)
        ?.map(i => ({
          label: i.name.english,
          value: i.snake,
        })) ?? [],
    [snakeItems]
  );

  useEffect(() => {
    const query = search && search.length > 1 ? search : condition.snakeName;

    if (query) {
      setItems(
        data.filter(d => d.label.toLowerCase().includes(query.toLowerCase()))
      );
    } else {
      setItems([]);
    }
  }, [data, search, condition.snakeName]);

  const handleSelect = (selectedItem: Item) => {
    const editedPayload = {
      snake: selectedItem.value,
      snakeName: selectedItem.label,
    };
    response(editedPayload);
  };

  return (
    <ConditionSearch
      label={condition.snakeName}
      dataTestId="scenario-data-list"
      onChange={e => setSearch(e.target.value)}
    >
      {items.map(item => (
        <S.Item
          key={item.value}
          data-testid={`condition-data-${item.value}`}
          onClick={() => handleSelect(item)}
          role="option"
        >
          {item.label}
        </S.Item>
      ))}
    </ConditionSearch>
  );
};
